// ============================================================================
// SECURITY EVENTS SERVICE - frontend/src/services/securityEvents.ts
// API calls for the security events admin dashboard (system admin only)
// ============================================================================

import { api } from './api';

export type SecurityEventType =
  | 'failed_auth'
  | 'rate_limit_violation'
  | 'ip_whitelist_violation'
  | 'unauthorized_access'
  | 'privilege_escalation'
  | 'suspicious_activity'
  | 'data_breach_attempt'
  | 'tenant_isolation_violation'
  | 'secrets_access_unauthorized'
  | 'policy_violation';

export type SecurityEventSeverity = 'low' | 'medium' | 'high' | 'critical';

export type SecurityResponseAction =
  | 'none'
  | 'log_only'
  | 'alert'
  | 'block_ip'
  | 'suspend_user'
  | 'revoke_session'
  | 'notify_admin'
  | 'trigger_incident';

export interface SecurityEvent {
  eventId: string;
  timestamp: string;
  eventType: SecurityEventType;
  severity: SecurityEventSeverity;
  sourceIp: string;
  userId?: string;
  organizationId?: string;
  description: string;
  metadata?: Record<string, any>;
  responseAction?: SecurityResponseAction;
  responseExecuted?: boolean;
  responseTimestamp?: string;
}

export interface SecurityEventsFilters {
  limit?: number;
  cursor?: string;
  eventType?: SecurityEventType;
  severity?: SecurityEventSeverity;
  userId?: string;
}

/**
 * List recent security events, newest first.
 */
export async function listSecurityEvents(
  filters?: SecurityEventsFilters
): Promise<{ events: SecurityEvent[]; nextCursor?: string }> {
  const params = new URLSearchParams();
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.cursor) params.append('cursor', filters.cursor);
  if (filters?.eventType) params.append('eventType', filters.eventType);
  if (filters?.severity) params.append('severity', filters.severity);
  if (filters?.userId) params.append('userId', filters.userId);

  const response = await api.get(`/api/admin/security-events?${params.toString()}`);
  return response.data;
}

/**
 * Reference data for the events filter UI.
 */
export async function getSecurityEventsMeta(): Promise<{
  eventTypes: SecurityEventType[];
  severities: SecurityEventSeverity[];
}> {
  const response = await api.get('/api/admin/security-events/meta');
  return response.data;
}
